import React from 'react';
import { FiClock } from 'react-icons/fi';
import { useHilReader, useHilQueue } from '@bizfirst/hil-react';
import { HilCountdown, HilPresentationRenderer } from '@bizfirst/hil-ui';
import { HilChatWindow } from '@bizfirst/hil-ui-chat-window';
import type { HilChatWindowSettings } from '@bizfirst/hil-ui-chat-window';
import { useUIStore } from '@flow-studio/store';

// Renders inline inside the dock tab's own flex column — no overlay chrome, no
// backdrop. Header is disabled here (visibility.header: false) because
// HilObservePanel now draws one shared external header (badge + title +
// countdown) for chat, form, and approval alike, instead of chat drawing its
// own plain title-only header via HilChatWindowChrome while form/approval
// drew a badged one — that mismatch was why chat looked bare next to form's
// pill-badge header in the dock tab. No close control: dismissal goes through
// the dock tab's own controls, not the session content itself.
const dockedChatSettings: HilChatWindowSettings = {
  actionBar: { presetClickBehavior: 'send' },
  visibility: { header: false, timestamps: true, avatars: true, presetRow: true, actionBar: true },
  mount: { mode: 'inline' },
  windowControls: { fullscreen: false, dock: false, minimize: false, close: false },
};

/**
 * Renders the active HIL event (form, chat, or approval) inside the FlowObserver
 * "HIL Actions" dock tab.
 *
 * Reads the same @bizfirst/hil-react store that drives HilOverlay/HilChatWindow
 * (the popups previously mounted at the WorkflowApp root — see WorkflowApp.tsx),
 * so the session survives the dock tab being closed/collapsed: dismissing the tab
 * only hides this view, it never touches the underlying HIL session. Reopening the
 * tab (via the TabBar's "+" menu or the hil-pending footer badge) shows the same
 * active session again — see useHilPanelBridge for the auto-open/badge wiring.
 *
 * Registered as the HIL Actions tab content in FlowObserverInit.tsx.
 */
export const HilObservePanel: React.FC = () => {
  const reader = useHilReader();
  const { active, count } = useHilQueue();
  const { theme, hilFormsVisible, hilChatVisible } = useUIStore();

  const bgPrimary = theme === 'dark' ? '#0f172a' : '#f8fafc';
  const bgSecondary = theme === 'dark' ? '#1a2332' : '#f1f5f9';
  const border = theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
  const textPrimary = theme === 'dark' ? '#e2e8f0' : '#1f2937';
  const textSecondary = theme === 'dark' ? '#9ca3af' : '#4b5563';
  const textTertiary = '#6b7280';

  const emptyState = (title: string, subtitle: string) => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      minHeight: '160px',
      color: textTertiary,
      fontSize: '13px',
      gap: '8px',
      padding: '24px',
      textAlign: 'center',
      background: bgPrimary,
    }}>
      <FiClock size={32} strokeWidth={1.5} />
      <span style={{ fontWeight: 500, color: textSecondary }}>{title}</span>
      <span style={{ fontSize: '12px' }}>{subtitle}</span>
    </div>
  );

  if (!active || !reader) {
    return emptyState(
      'No active HIL event',
      'Forms, chats, and approval requests will appear here when a workflow suspends.'
    );
  }

  const hilType = reader.hilType().toLowerCase();
  const isChat = hilType === 'chat';
  const visible = isChat ? hilChatVisible : hilFormsVisible;

  if (!visible) {
    return emptyState(
      isChat ? 'Chat window is hidden' : 'HIL forms are hidden',
      'Re-enable it from the View menu to respond.'
    );
  }

  // reader.title() returns '' (not null) when the node's Title setting was left blank —
  // ChatNodeExecutor/etc. serialize it as `mySettings.Title ?? string.Empty`, never null —
  // so `?? reader.hilType()` never fires on the empty-string case. Use `||` to also fall
  // back on empty string, or the header shows a blank title next to the badge.
  const title = reader.title() || reader.hilType();
  const deadline = reader.deadline();
  const pendingCount = count - 1;

  // Chat intentionally reuses the same badge class as form (not hil-badge--chat's purple) —
  // this dock-tab header should look uniform across HIL types, not color-coded by type.
  const badgeClass = hilType === 'approval'
    ? 'hil-badge hil-badge--approval'
    : hilType === 'form' || hilType === 'chat'
    ? 'hil-badge hil-badge--form'
    : 'hil-badge hil-badge--info';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: bgPrimary }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '9px 14px',
        borderBottom: `1px solid ${border}`,
        background: bgSecondary,
        flexShrink: 0,
      }}>
        <span className={badgeClass}>{reader.hilType()}</span>
        <span style={{ fontSize: '13px', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: textPrimary }}>
          {title}
        </span>
        {deadline && <HilCountdown expiresAt={deadline} />}
      </div>

      {isChat ? (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <HilChatWindow settings={dockedChatSettings} />
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px' }}>
          {reader.description() && (
            <p style={{ fontSize: '13px', color: textSecondary, marginTop: 0 }}>{reader.description()}</p>
          )}
          <HilPresentationRenderer />
        </div>
      )}

      {pendingCount > 0 && (
        <div style={{ borderTop: `1px solid ${border}`, padding: '8px 14px', background: bgSecondary, flexShrink: 0 }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: textSecondary }}>
            {pendingCount} more task{pendingCount > 1 ? 's' : ''} pending
          </span>
        </div>
      )}
    </div>
  );
};
